import { Icon } from "@iconify/react";
import { useLocation, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useI18n } from "@/locales/i18n-provider";
import type { NavigationItem } from "@/surfaces/standalone/navigation/navigation-model";
import { cn } from "@/utils";

export function NavigationList({ collapsed = false, items, onNavigate }: { collapsed?: boolean; items: NavigationItem[]; onNavigate?: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useI18n();

  return (
    <div className="space-y-1">
      {items.map((item) => {
        const isActive = item.href ? location.pathname === item.href || location.pathname.startsWith(`${item.href}/`) : item.active;
        const label = t(item.labelKey);

        return (
          <Tooltip key={item.labelKey}>
            <TooltipTrigger asChild>
              <Button
                aria-label={collapsed ? label : undefined}
                className={cn(
                  "h-9 w-full gap-3 rounded-lg hover:text-primary/80 hover:bg-card/80 cursor-pointer",
                  collapsed ? "justify-center px-0" : "justify-start px-3",
                  isActive && "bg-card text-primary hover:bg-card hover:text-primary",
                )}
                variant="ghost"
                onClick={() => {
                  if (item.href) {
                    navigate(item.href);
                  }
                  onNavigate?.();
                }}
              >
                <Icon className={cn("size-4", collapsed && "text-foreground", isActive && "text-primary")} icon={item.icon} />
                {collapsed ? null : <span className="flex-1 text-left">{label}</span>}
                {!collapsed && item.badge ? <Badge variant="secondary">{item.badge}</Badge> : null}
              </Button>
            </TooltipTrigger>
            {collapsed ? (
              <TooltipContent side="right" sideOffset={8}>
                {label}
              </TooltipContent>
            ) : null}
          </Tooltip>
        );
      })}
    </div>
  );
}
